import { createAction } from '@reduxjs/toolkit'
import { call, delay, put, select, takeLatest } from 'redux-saga/effects'

import { deleteAiSuggestionsByMappingIds } from '../../../api/projects-api'
import { TOASTER_TIMER } from '../../../components/projects/project-settings/utils/constants'
import { AIAutomatedObject } from '../../../models/aiClassifications'
import { DocumentMapping } from '../../../models/documentMapping'
import { Project } from '../../../models/project'
import { ToastPosition, ToastType } from '../../../shared/components/toast/toast'
import { deleteAISuggestions, selectAiSuggestions } from '../../slices/2D'
import { selectDocumentChunkIdsToRemoveAiSuggestions, setToastData } from '../../slices/common'
import { selectDocumentMappings } from '../../slices/documents'
import { selectProject } from '../../slices/project'

export const bulkDeleteAISuggestionsAction = createAction<string>('bulkDeleteAISuggestionsAction')

export function* handleBulkDeleteAISuggestions({ payload }: ReturnType<typeof bulkDeleteAISuggestionsAction>) {
    const project: Project = yield select(selectProject)
    const documentChunksWithAiSuggestions: number[] | null = yield select(selectDocumentChunkIdsToRemoveAiSuggestions)
    const aiSuggestions: AIAutomatedObject[] | null = yield select(selectAiSuggestions)
    const documentMappings: DocumentMapping[] | null = yield select(selectDocumentMappings)

    if (!documentChunksWithAiSuggestions || !documentMappings || !aiSuggestions) return

    const selectedDocumentMappingIds = documentMappings
        .filter((m) => documentChunksWithAiSuggestions.includes(m.document_chunk_id))
        .map((m) => m.id)

    const filteredAiSuggestionIds = aiSuggestions.reduce<string[]>((acc, ai) => {
        if (selectedDocumentMappingIds.includes(ai.document_mapping_id)) {
            acc.push(ai.id)
        }

        return acc
    }, [])

    try {
        yield call(deleteAiSuggestionsByMappingIds, project.id, selectedDocumentMappingIds, payload)

        yield put(deleteAISuggestions(filteredAiSuggestionIds))

        yield put(
            setToastData({
                visible: true,
                type: ToastType.SUCCESS,
                isSuccess: true,
                body: 'AI suggestions removed.',
                position: ToastPosition.BOTTOM,
            })
        )

        yield delay(TOASTER_TIMER)

        yield put(setToastData({ visible: false }))
    } catch (e) {
        console.error('Error on bulk delete of AI suggestions', e)

        yield put(
            setToastData({
                visible: true,
                type: ToastType.ERROR,
                isSuccess: false,
                body: 'Error deleting AI suggestions.',
                position: ToastPosition.BOTTOM,
            })
        )

        yield delay(TOASTER_TIMER)

        yield put(setToastData({ visible: false }))
    }
}

export function* watchForBulkDeleteAISuggestion() {
    yield takeLatest(bulkDeleteAISuggestionsAction.type, handleBulkDeleteAISuggestions)
}
